import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import axios from 'helpers/api';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';

const getDatePart = value =>
  String(value || '')
    .trim()
    .split(/[T\s]/)[0];

// Fax dates arrive as M/D/YYYY (with or without leading zeros) or
// YYYY-MM-DD; show them as MM/DD/YY.
const FAX_DATE_FORMATS = [
  'M/D/YYYY',
  'MM/DD/YYYY',
  'M/DD/YYYY',
  'MM/D/YYYY',
  'YYYY-MM-DD'
];

const formatFaxDate = value => {
  const datePart = getDatePart(value);
  const parsed = moment(datePart, FAX_DATE_FORMATS, true);
  return parsed.isValid() ? parsed.format('MM/DD/YY') : datePart;
};

const formatFaxDateTime = ({ fax_date: faxDate, fax_time: faxTime }) =>
  [formatFaxDate(faxDate), faxTime].filter(Boolean).join(' ') || '—';

// Fax date + time as a timestamp, or null when it can't be read.
const getFaxTime = ({ fax_date: faxDate, fax_time: faxTime }) => {
  const datePart = getDatePart(faxDate);
  const timePart = String(faxTime || '').trim() || '00:00:00';
  const match = datePart.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

  let timestamp;
  if (match) {
    const [, month, day, year] = match;
    const [hours = 0, minutes = 0, seconds = 0] = timePart
      .split(':')
      .map(Number);
    timestamp = new Date(
      year,
      month - 1,
      day,
      hours,
      minutes,
      seconds
    ).getTime();
  } else {
    timestamp = new Date(`${datePart}T${timePart}`).getTime();
  }
  return Number.isNaN(timestamp) ? null : timestamp;
};

const getSortTime = row => getFaxTime(row) ?? Number.MAX_SAFE_INTEGER;

const parseSyncedAt = value => {
  const match = String(value || '')
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const [, year, month, day, hours, minutes, seconds = 0] = match;
  return new Date(year, month - 1, day, hours, minutes, seconds).getTime();
};

const HOUR_MS = 60 * 60 * 1000;

// Age tiles. Each "Older than" count includes everything older, so they
// overlap; "Within 24H" is the rest. Clicking a tile filters the table.
const AGE_TILES = [
  {
    id: 'over96',
    label: 'Older than 96H',
    tone: 'danger',
    test: hours => hours > 96
  },
  {
    id: 'over72',
    label: 'Older than 72H',
    tone: 'warning',
    test: hours => hours > 72
  },
  {
    id: 'over48',
    label: 'Older than 48H',
    tone: 'warning',
    test: hours => hours > 48
  },
  {
    id: 'over24',
    label: 'Older than 24H',
    tone: 'info',
    test: hours => hours > 24
  },
  {
    id: 'within24',
    label: 'Within 24H',
    tone: 'success',
    test: hours => hours <= 24
  }
];

const formatLastSyncedAt = value => {
  if (!value) return 'Updated —';

  const match = String(value)
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?$/);
  if (!match) return `Updated ${value}`;

  const [, year, month, day, hours, minutes] = match;
  return `Updated ${month}/${day}/${year.slice(-2)}, ${hours}:${minutes}`;
};

const getResponsePayload = response => {
  const body = response?.data;
  if (
    Array.isArray(body?.data) ||
    Object.prototype.hasOwnProperty.call(body || {}, 'total')
  ) {
    return body;
  }
  return body?.data || body || {};
};

// Oldest fax first, so the drug that has waited longest is on top.
const columns = [
  {
    id: 'fax_date_time',
    accessorFn: formatFaxDateTime,
    header: 'Fax Date & Time',
    sortingFn: (rowA, rowB) =>
      getSortTime(rowA.original) - getSortTime(rowB.original),
    meta: {
      headerProps: { className: 'text-900 text-start' },
      cellProps: { className: 'text-start font-monospace' }
    }
  },
  {
    id: 'drug',
    accessorFn: row => row.drug || '—',
    header: 'Drug',
    meta: {
      headerProps: { className: 'text-900 text-start' },
      cellProps: { className: 'text-start fw-semibold text-900' }
    }
  },
  {
    id: 'patient',
    accessorFn: row => row.patient || '—',
    header: 'Patient',
    meta: {
      headerProps: { className: 'text-900 text-start' },
      cellProps: { className: 'text-start' }
    }
  },
  {
    id: 'facility_name',
    accessorFn: row => row.Facility_name || '—',
    header: 'Facility',
    meta: {
      headerProps: { className: 'text-900 text-start' },
      cellProps: { className: 'text-start' }
    }
  }
];

const UrgentDrugsWidget = () => {
  const [urgentDrugs, setUrgentDrugs] = useState([]);
  const [total, setTotal] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [ageFilter, setAgeFilter] = useState(null);

  const loadUrgentDrugs = useCallback(async () => {
    try {
      const response = await axios.get('/dashboard/urgent-drugs');
      const payload = getResponsePayload(response);
      const drugs = Array.isArray(payload?.data) ? payload.data : [];

      setUrgentDrugs(drugs);
      setTotal(Number(payload?.total) || 0);
      setLastSyncedAt(payload?.last_synced_at || '');
      setError('');
    } catch (requestError) {
      setError('Unable to load urgent drugs. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUrgentDrugs();
  }, [loadUrgentDrugs]);

  // Hours since each fax, measured against the last sync: it uses the same
  // clock as the fax times, whatever time zone the browser is in.
  const ageHours = useMemo(() => {
    const reference = parseSyncedAt(lastSyncedAt) ?? Date.now();
    return urgentDrugs.map(drug => {
      const faxTime = getFaxTime(drug);
      return faxTime === null ? null : (reference - faxTime) / HOUR_MS;
    });
  }, [urgentDrugs, lastSyncedAt]);

  const ageTiles = useMemo(
    () =>
      AGE_TILES.map(tile => ({
        ...tile,
        count: ageHours.filter(hours => hours !== null && tile.test(hours))
          .length
      })),
    [ageHours]
  );

  const visibleDrugs = useMemo(() => {
    const tile = AGE_TILES.find(({ id }) => id === ageFilter);
    if (!tile) return urgentDrugs;
    return urgentDrugs.filter(
      (_, i) => ageHours[i] !== null && tile.test(ageHours[i])
    );
  }, [urgentDrugs, ageHours, ageFilter]);

  const table = useAdvanceTable({
    data: visibleDrugs,
    columns,
    selection: false,
    sortable: true,
    pagination: true,
    perPage: 15,
    initialState: { sorting: [{ id: 'fax_date_time', desc: false }] }
  });

  const hasUrgentDrugs = total > 0 && urgentDrugs.length > 0;
  const hasMatches = table.getRowModel().rows.length > 0;

  return (
    <Card className="h-100 overflow-hidden shadow-sm">
      <Card.Header className="pb-0 d-flex flex-wrap align-items-start justify-content-between gap-2">
        <div>
          <h6 className="mb-0 mt-1 text-900">Urgent Drugs</h6>
          <div className="fs-11 text-500">
            {formatLastSyncedAt(lastSyncedAt)}
          </div>
        </div>
      </Card.Header>
      <Card.Body className="pt-3">
        {isLoading ? (
          <div className="py-4 text-center text-500">
            <FontAwesomeIcon icon="spinner" spin className="me-2" />
            Loading urgent drugs...
          </div>
        ) : error ? (
          <div className="py-4 text-center text-danger">{error}</div>
        ) : !hasUrgentDrugs ? (
          <div className="py-4 text-center text-500 bg-100 rounded-2">
            No urgent drugs pending.
          </div>
        ) : (
          <AdvanceTableProvider {...table}>
            <Row className="g-2 mb-3 row-cols-2 row-cols-sm-3 row-cols-lg-5">
              {ageTiles.map(tile => {
                const isActive = ageFilter === tile.id;
                return (
                  <Col key={tile.id}>
                    <button
                      type="button"
                      className={classNames(
                        'd-block w-100 h-100 text-start rounded-3 border px-3 py-2',
                        isActive
                          ? `border-${tile.tone} bg-${tile.tone} bg-opacity-10`
                          : 'border-200 bg-transparent'
                      )}
                      onClick={() => setAgeFilter(isActive ? null : tile.id)}
                      disabled={!tile.count}
                      aria-pressed={isActive}
                    >
                      <span className="d-block fs-11 fw-semibold text-600">
                        {tile.label}
                      </span>
                      <span
                        className={classNames(
                          'd-block fs-6 fw-bold lh-sm',
                          tile.count ? 'text-900' : 'text-400'
                        )}
                      >
                        {tile.count}
                      </span>
                    </button>
                  </Col>
                );
              })}
            </Row>
            <Row className="align-items-center mb-3 g-2">
              <Col xs={12} sm={8} md={6} xxl={8}>
                <AdvanceTableSearchBox placeholder="Search drug, patient, facility..." />
              </Col>
              {ageFilter && (
                <Col xs="auto">
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 fs-10"
                    onClick={() => setAgeFilter(null)}
                  >
                    Show all
                  </button>
                </Col>
              )}
            </Row>
            <AdvanceTable
              headerClassName="bg-200 text-nowrap align-middle text-center"
              rowClassName="align-middle text-center"
              tableProps={{
                size: 'sm',
                striped: true,
                className: 'fs-10 mb-0 overflow-hidden'
              }}
            />
            {hasMatches ? (
              <div className="mt-3">
                <AdvanceTableFooter
                  rowInfo
                  navButtons
                  rowsPerPageSelection
                  rowsPerPageOptions={[15, 25, 50]}
                />
              </div>
            ) : (
              <div className="py-3 text-center text-500 fs-10">
                No urgent drugs match your search.
              </div>
            )}
          </AdvanceTableProvider>
        )}
      </Card.Body>
    </Card>
  );
};

export default UrgentDrugsWidget;
